import React, { useEffect, useState } from 'react';
import { hr } from '../../face/hrApi';

const FIELDS = [
  { group: 'Check-in / check-out', items: [
    { k: 'cooldownSeconds', label: 'Repeat-scan window (seconds)', hint: 'A second scan of the same person inside this time shows "already recorded".', t: 'int' },
    { k: 'outWindowMinutes', label: 'Check-out opens before shift end (minutes)', hint: 'Before this, a scan after check-in just says "already checked in".', t: 'int' },
    { k: 'minWorkMinutes', label: 'Minimum time after check-in before check-out (minutes)', hint: 'Same 1-hour rule as the phone clock-out.', t: 'int' },
  ] },
  { group: 'Recognition strictness', items: [
    { k: 'matchThreshold', label: 'Match distance (lower = stricter)', hint: 'Default 0.45. Raise slightly only if known staff are rejected.', t: 'num', step: 0.01 },
    { k: 'matchMargin', label: 'Gap to the runner-up person', hint: 'Protects against look-alikes. Default 0.06.', t: 'num', step: 0.01 },
    { k: 'minProbes', label: 'Face samples per attempt', hint: 'Default 3.', t: 'int' },
    { k: 'consensus', label: 'Share of samples that must agree', hint: 'Default 0.6.', t: 'num', step: 0.05 },
    { k: 'enrollDuplicateThreshold', label: 'Duplicate-face check when enrolling', hint: 'Blocks enrolling the same face under two employees.', t: 'num', step: 0.01 },
  ] },
  { group: 'Liveness (anti-photo / anti-video)', items: [
    { k: 'requireLiveness', label: 'Ask the person to blink / turn their head', hint: 'Keep ON. Turn off only temporarily if the kiosk rejects genuine people.', t: 'bool' },
    { k: 'livenessSteps', label: 'Number of actions asked (1-3)', hint: 'Default 2.', t: 'int' },
    { k: 'challengeTtlSeconds', label: 'Time allowed per attempt (seconds)', hint: 'Default 45.', t: 'int' },
  ] },
  { group: 'Image quality', items: [
    { k: 'minFaceWidthRatio', label: 'Minimum face size (share of frame width)', hint: 'Default 0.16 - "come closer" below this.', t: 'num', step: 0.01 },
    { k: 'minDetectionScore', label: 'Minimum detection score', hint: 'Default 0.55.', t: 'num', step: 0.01 },
    { k: 'minSharpness', label: 'Minimum sharpness', hint: 'Default 15.', t: 'num', step: 1 },
    { k: 'minBrightness', label: 'Minimum brightness', hint: 'Default 45.', t: 'num', step: 1 },
    { k: 'maxBrightness', label: 'Maximum brightness', hint: 'Default 200.', t: 'num', step: 1 },
  ] },
  { group: 'Privacy & records', items: [
    { k: 'storeSnapshots', label: 'Keep a small photo of each kiosk attempt', hint: 'Lets HR check who was at the kiosk. Enrolment photos are never stored.', t: 'bool' },
    { k: 'snapshotRetentionDays', label: 'Delete kiosk photos after (days)', hint: 'Default 90.', t: 'int' },
    { k: 'storeAttributes', label: 'Record estimated age / gender / mood', hint: 'Off by default; not needed for attendance.', t: 'bool' },
  ] },
];

export default function FaceSettingsTab() {
  const [vals, setVals] = useState(null);
  const [orig, setOrig] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => { try { const r = await hr('/settings'); setVals(r.settings); setOrig(r.settings); } catch (e) { setMsg({ bad: true, t: e.message }); } })(); }, []);

  const save = async () => {
    const patch = {};
    FIELDS.forEach((g) => g.items.forEach((f) => {
      if (vals[f.k] !== orig[f.k]) patch[f.k] = f.t === 'bool' ? !!vals[f.k] : Number(vals[f.k]);
    }));
    if (!Object.keys(patch).length) { setMsg({ t: 'No changes to save.' }); return; }
    setBusy(true); setMsg(null);
    try { const r = await hr('/settings', { method: 'PUT', body: patch }); setVals(r.settings); setOrig(r.settings); setMsg({ ok: true, t: 'Saved. Kiosks pick this up within a minute.' }); }
    catch (e) { setMsg({ bad: true, t: e.message }); }
    finally { setBusy(false); }
  };

  if (!vals) return <div className="fc-card fc-muted">{msg ? msg.t : 'Loading…'}</div>;
  return (
    <div>
      {FIELDS.map((g) => (
        <div className="fc-card" key={g.group}>
          <h3>{g.group}</h3>
          <div className="fc-settings">
            {g.items.map((f) => (
              <label key={f.k} className="fc-setting">
                <span className="fc-setting-l"><b>{f.label}</b><span className="fc-muted fc-small">{f.hint}</span></span>
                {f.t === 'bool'
                  ? <input type="checkbox" checked={!!vals[f.k]} onChange={(e) => setVals({ ...vals, [f.k]: e.target.checked })} />
                  : <input type="number" step={f.step || 1} value={vals[f.k]} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} />}
              </label>
            ))}
          </div>
        </div>
      ))}
      {msg && <div className={`fc-callout ${msg.bad ? 'fc-bad' : msg.ok ? 'fc-ok' : ''}`}>{msg.t}</div>}
      <button className="fc-btn fc-btn-red" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</button>
    </div>
  );
}
